import { LOCALE_NAMES, LOCALES } from '@hm/contracts'
import type { ComponentProps } from 'react'
import { useLocaleChoice } from '~/lib/i18n'
import { cn } from '~/lib/utils'

/**
 * English or नेपाली, as a pair of buttons rather than a dropdown.
 *
 * Two options do not earn a menu, and — more to the point — somebody who has landed
 * on the wrong language needs to see the way out without opening anything. Each
 * name is written in its own script for the same reason: "Nepali" is no help to a
 * reader who cannot read the word.
 *
 * `onPersist` is optional so this works on the sign-in screen, where there is no
 * account to remember the choice against yet.
 */
export function LanguageSwitch({
  className,
  onPersist,
  ...props
}: ComponentProps<'fieldset'> & {
  onPersist?: (locale: (typeof LOCALES)[number]) => void
}) {
  const { locale, choose } = useLocaleChoice(onPersist)

  return (
    <fieldset
      className={cn('flex rounded-control border border-border bg-surface-muted p-0.5', className)}
      {...props}
    >
      <legend className="sr-only">Language / भाषा</legend>
      {LOCALES.map((value) => (
        <button
          key={value}
          type="button"
          lang={value}
          onClick={() => choose(value)}
          aria-pressed={locale === value}
          className={cn(
            // Never wrap: "नेपाली" breaking across two lines in a narrow sidebar is
            // the one thing that would make the way out of the wrong language
            // harder to read than the language itself.
            'h-8 flex-1 rounded-[0.4375rem] px-1.5 text-xs font-bold whitespace-nowrap transition-colors',
            locale === value
              ? 'bg-surface text-primary shadow-card'
              : 'text-ink-subtle hover:text-ink',
          )}
        >
          {LOCALE_NAMES[value]}
        </button>
      ))}
    </fieldset>
  )
}
